import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropertiesList from "../../Properties/PropertiesList";
import "./SearchResults.css";
import Navigation from "../Navigation/Navigation";
import FilterBar from "./FilterBar";

const SearchResults = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const transactionType = searchParams.get("transaction_type") || "1";  // "1" for Rent, "2" for Buy
  const [filters, setFilters] = useState({
    location: searchParams.get("location") || "",
    minPrice: searchParams.get("min_price") || "",
    maxPrice: searchParams.get("max_price") || "",
    minBedrooms: searchParams.get("min_bedrooms") || "",
    maxBedrooms: searchParams.get("max_bedrooms") || "",
  });

  const propertiesPerPage = 21;
  const [allProperties, setAllProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOption, setSortOption] = useState("lowest");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showSoldLet, setShowSoldLet] = useState(true);  // Toggle to show Sold/Let Agreed

  const sortProperties = (properties) => {
    return [...properties].sort((a, b) => {
      // Always show available properties first
      if (a.status_id === 0 && b.status_id !== 0) return -1;
      if (b.status_id === 0 && a.status_id !== 0) return 1;

      // Apply sorting based on selected sort option
      switch (sortOption) {
        case "highest":
          return b.price - a.price;
        case "lowest":
          return a.price - b.price;
        case "justAdded":
          return new Date(b.create_date) - new Date(a.create_date);
        default:
          return 0;
      }
    });
  };

  const fetchProperties = async (appliedFilters = filters) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const params = new URLSearchParams();
      params.append("page_size", 100);
      params.append("trans_type_id", transactionType); // Transaction type (rent or buy)

      if (appliedFilters.minPrice) params.append("min_price", appliedFilters.minPrice);
      if (appliedFilters.maxPrice) params.append("max_price", appliedFilters.maxPrice);
      if (appliedFilters.minBedrooms) params.append("min_bedrooms", appliedFilters.minBedrooms);
      if (appliedFilters.maxBedrooms) params.append("max_bedrooms", appliedFilters.maxBedrooms);

      const url = `https://webapi.10ninety.co.uk/properties/?${params.toString()}`;
      console.log("Fetching properties from:", url);

      const response = await fetch(url, {
        headers: {
          "10ninety-webapi-key": process.env.REACT_APP_API_KEY,
        },
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      const data = await response.json();
      let sortedData = sortProperties(data.properties);

      // Apply location filtering
      if (appliedFilters.location) {
        let searchTerm = appliedFilters.location.toLowerCase().trim();
        sortedData = sortedData.filter((property) => {
          let postcodePrefix = property.postcode_1?.toLowerCase() || "";
          let county = property.county?.toLowerCase() || "";
          let town = property.address_2?.toLowerCase() || "";
          return (
            postcodePrefix.startsWith(searchTerm) ||
            county.includes(searchTerm) ||
            town.includes(searchTerm)
          );
        });
      }

      // Filter based on status (only show available by default, unless toggled to show all)
      if (transactionType === "1") {  // Rent properties
        sortedData = sortedData.filter((property) => {
          return showSoldLet || property.status_id === 0;  // Show Let/Let Agreed if toggled on
        });
      } else if (transactionType === "2") {  // Buy properties
        sortedData = sortedData.filter((property) => {
          return showSoldLet || property.status_id === 0;  // Show Sold/STC if toggled on
        });
      }

      setAllProperties(sortedData);
      setTotalPages(Math.ceil(sortedData.length / propertiesPerPage));
      setCurrentPage(1);
      updateDisplayedProperties(sortedData, 1);
    } catch (error) {
      console.error("Error fetching properties:", error);
      setErrorMessage("Failed to load properties. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, [transactionType, showSoldLet]);

  useEffect(() => {
    updateDisplayedProperties(allProperties, currentPage);
  }, [currentPage, allProperties]);

  const updateDisplayedProperties = (properties, page) => {
    const startIndex = (page - 1) * propertiesPerPage;
    setFilteredProperties(properties.slice(startIndex, startIndex + propertiesPerPage));
  };

  useEffect(() => {
    setAllProperties(sortProperties(allProperties));
    updateDisplayedProperties(sortProperties(allProperties), currentPage);
  }, [sortOption]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchProperties(newFilters);
  };

  useEffect(() => {
    console.log("Updated filters in SearchResults:", filters);
    fetchProperties(filters);
  }, [filters]);

  return (
    <div className="searchResultsContainer">
      <Navigation linkColor="black" submenuColor="black" menuIconColor="black"/>
      <div className="searchContent">
        <div className="filterBarContainer">
          <FilterBar onFilterChange={handleFilterChange} transactionType={transactionType} />
        </div>
        <div className="propertySection">
          <div className="toggleBar">
            <label>
              <input type="checkbox" checked={showSoldLet} onChange={() => setShowSoldLet(!showSoldLet)} />
              Show {transactionType === "1" ? "Sold / SSTC Properties" : "Let / Let Agreed Properties" }
            </label>
          </div>
          <div className="sortFilter">
            <select id="sort" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
              <option value="lowest">Lowest Price</option>
              <option value="highest">Highest Price</option>
              <option value="justAdded">Just Added</option>
            </select>
          </div>
          <div className="propertiesList">
            {loading && <p>Loading...</p>}
            {errorMessage && <p className="errorMessage">{errorMessage}</p>}
            {!loading && filteredProperties.length === 0 && !errorMessage && <p>No properties found.</p>}
            {!loading &&
              filteredProperties.length > 0 &&
              filteredProperties.map((property) => <PropertiesList key={property.property_ref} {...property} />)}
          </div>
          <div className="pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => setCurrentPage(index + 1)}
                className={currentPage === index + 1 ? "active" : ""}
              >
                {index + 1}
              </button>
            ))}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
